import React from 'react';

// Sayfa listesi: çok sayfa olduğunda ilk/son sayfa + aktif sayfanın komşuları gösterilir,
// aradaki boşluklar "…" ile kısaltılır. Örn: 1 … 4 5 6 … 25
export const buildPageItems = (pageCount, currentPage, siblings = 1) => {
  if (pageCount <= 0) return [];
  const totalSlots = siblings * 2 + 5; // ilk + son + aktif + komşular + 2 boşluk
  if (pageCount <= totalSlots) {
    return Array.from({ length: pageCount }, (_, i) => i);
  }
  const last = pageCount - 1;
  let start = Math.max(1, currentPage - siblings);
  let end = Math.min(last - 1, currentPage + siblings);
  // Kenarlara yakınken gösterilen buton sayısı sabit kalsın
  if (currentPage - siblings <= 2) {
    start = 1;
    end = siblings * 2 + 2;
  } else if (currentPage + siblings >= last - 2) {
    start = last - (siblings * 2 + 2);
    end = last - 1;
  }
  const items = [0];
  if (start > 1) items.push('start-gap');
  for (let i = start; i <= end; i += 1) items.push(i);
  if (end < last - 1) items.push('end-gap');
  items.push(last);
  return items;
};

// detached: tablo kartına yapışık değil, altında ayrı bir kutu olarak durur
const ListPagination = ({ pageCount, currentPage, onPageChange, totalItems, itemsPerPage, itemLabel = 'kayıt', detached = false }) => {
  if (!pageCount || pageCount <= 0) return null;

  const goTo = (page) => {
    const next = Math.min(Math.max(page, 0), pageCount - 1);
    if (next !== currentPage) onPageChange(next);
  };

  const from = totalItems > 0 ? currentPage * itemsPerPage + 1 : 0;
  const to = Math.min(totalItems, (currentPage + 1) * itemsPerPage);

  return (
    <div className={`lp-bar${detached ? ' lp-detached' : ''}`}>
      <div className="lp-info">
        {typeof totalItems === 'number' && itemsPerPage
          ? <><strong>{from}–{to}</strong> / {totalItems} {itemLabel}</>
          : <>Sayfa <strong>{currentPage + 1}</strong> / {pageCount}</>}
      </div>
      <nav className="lp-pages" aria-label="Sayfalama">
        <button
          type="button"
          className="lp-btn lp-nav"
          onClick={() => goTo(currentPage - 1)}
          disabled={currentPage === 0}
          aria-label="Önceki sayfa"
        >
          ‹
        </button>
        {buildPageItems(pageCount, currentPage).map((item) =>
          typeof item === 'number' ? (
            <button
              type="button"
              key={item}
              className={`lp-btn${item === currentPage ? ' lp-active' : ''}`}
              onClick={() => goTo(item)}
              aria-current={item === currentPage ? 'page' : undefined}
            >
              {item + 1}
            </button>
          ) : (
            <span key={item} className="lp-gap" aria-hidden="true">…</span>
          )
        )}
        <button
          type="button"
          className="lp-btn lp-nav"
          onClick={() => goTo(currentPage + 1)}
          disabled={currentPage >= pageCount - 1}
          aria-label="Sonraki sayfa"
        >
          ›
        </button>
      </nav>
      <style>
        {`
          .lp-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            flex-wrap: wrap;
            padding: 10px 14px;
            margin-top: 8px;
            background: linear-gradient(180deg, #f9fbff 0%, #f2f7ff 100%);
            border-top: 1px solid #dbe5f0;
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
            box-sizing: border-box;
            width: 100%;
            font-family: 'Roboto', sans-serif;
          }
          .lp-detached {
            margin-top: 10px;
            border: 1px solid #d7e1ee;
            border-radius: 10px;
            box-shadow: 0 6px 14px rgba(15, 23, 42, 0.06);
          }
          .lp-info {
            font-size: 13px;
            color: #475569;
            white-space: nowrap;
          }
          .lp-info strong {
            color: #0f2744;
          }
          .lp-pages {
            display: flex;
            align-items: center;
            gap: 4px;
            flex-wrap: wrap;
            justify-content: center;
          }
          .lp-btn {
            min-width: 34px;
            height: 34px;
            padding: 0 8px;
            border: 1px solid #d5deea;
            border-radius: 8px;
            background: #fff;
            color: #0f172a;
            font-size: 14px;
            font-weight: 600;
            font-family: inherit;
            cursor: pointer;
            transition: background .15s ease, border-color .15s ease, box-shadow .15s ease;
          }
          .lp-btn:hover:not(:disabled):not(.lp-active) {
            background: #eef4ff;
            border-color: #b9c9df;
            box-shadow: 0 3px 8px rgba(15, 23, 42, 0.10);
          }
          .lp-btn:focus-visible {
            outline: 2px solid #b77d2f;
            outline-offset: 1px;
          }
          .lp-active {
            background: #0f2744;
            border-color: #0f2744;
            color: #fff;
            cursor: default;
          }
          .lp-nav {
            font-size: 20px;
            line-height: 1;
            color: #0f2744;
          }
          .lp-btn:disabled {
            opacity: .4;
            cursor: not-allowed;
          }
          .lp-gap {
            min-width: 20px;
            text-align: center;
            color: #94a3b8;
            font-weight: 700;
            user-select: none;
          }
          @media (max-width: 600px) {
            .lp-bar {
              justify-content: center;
              padding: 8px 6px;
            }
            .lp-info {
              width: 100%;
              text-align: center;
            }
            .lp-btn {
              min-width: 30px;
              height: 30px;
              font-size: 13px;
              padding: 0 6px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ListPagination;
